import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/components/feedback/useToast';
import { DashboardApiError, type DashboardPeriod } from '../types/dashboard.types';
import { useDashboardChartsQuery } from '../hooks/use-dashboard';
import { DiseaseGroupComboChart } from './DiseaseGroupComboChart';
import { InquiryBarChart } from './InquiryBarChart';

type ChartKey = 'patientsByDepartment' | 'inquiryStatus';

// The label key is mapped explicitly rather than built from the wire field name. `patientsByDepartment` is
// the API field, and since ADM.028 / SHCT-506 it carries 주진단(질병군) rather than 진료과 — deriving the
// display key from it would force the Korean title to keep a name that no longer describes what it shows.
const TITLE_KEY: Record<ChartKey, string> = {
  patientsByDepartment: 'dashboard.charts.patientsByDiseaseGroup',
  inquiryStatus: 'dashboard.charts.inquiryStatus',
};

// Each chart owns its own 주간/월간 tab (PRD 01 §2.2 #13). The query key embeds the period,
// so two cards on the same period dedupe to one fetch; switching one re-fetches only that period.
export function DashboardChartCard({ which }: { which: ChartKey }) {
  const { t } = useTranslation('admin');
  const [period, setPeriod] = useState<DashboardPeriod>('weekly');
  const { data, isLoading, isError, error } = useDashboardChartsQuery(period);
  const networkToasted = useRef(false);

  // PRD 01 §8.2: network failure → toast (chart area still shows the inline message).
  useEffect(() => {
    if (isError && error instanceof DashboardApiError && error.code === 'NETWORK' && !networkToasted.current) {
      networkToasted.current = true;
      toast.error(t('dashboard.network'));
    }
    if (!isError) networkToasted.current = false;
  }, [isError, error, t]);

  const chart = data?.[which];
  const isEmpty = !!chart && (chart.labels.length === 0 || chart.datasets.length === 0);

  return (
    <div className="chart-card">
      <div className="chart-card__header">
        <h3 className="chart-card__title">{t(TITLE_KEY[which])}</h3>
        <div className="chart-card__tabs">
          {(['weekly', 'monthly'] as const).map((p) => (
            <button
              key={p}
              type="button"
              className={`chart-card__tab ${period === p ? 'chart-card__tab--active' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {t(`dashboard.${p}`)}
            </button>
          ))}
        </div>
      </div>
      <div className="chart-card__body">
        {isLoading ? (
          // SHCT-481 — `role="status"` + label instead of `aria-hidden`: the skeleton IS
          // the loading indicator this screen offers, so hiding it from assistive tech
          // left non-sighted users with no signal at all that a fetch was in flight.
          <span
            className="skeleton-box"
            style={{ display: 'block', width: '100%', height: '100%' }}
            role="status"
            aria-label={t('dashboard.chartLoading')}
          />
        ) : isError || !chart ? (
          // PRD 01 §8.2: 차트 API 실패 → 에러 메시지만 표시(재시도 버튼 없음). 재시도는 요약 카드 전용.
          <div className="chart-card__message" role="alert">{t('dashboard.chartError')}</div>
        ) : isEmpty ? (
          <div className="chart-card__message">{t('dashboard.chartEmpty')}</div>
        ) : which === 'patientsByDepartment' ? (
          <DiseaseGroupComboChart chart={chart} />
        ) : (
          <InquiryBarChart chart={chart} />
        )}
      </div>
    </div>
  );
}
